import { useState, useMemo, useRef, useEffect } from 'react';

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}

function HaulTable({ hauls, bblPerIn, highlightedHaul, onHaulClick,
  showDeactivated, onToggleShowDeactivated, onToggleHaul }) {
  const [sortField, setSortField] = useState('drop_start');
  const [sortDir, setSortDir] = useState('desc');
  const highlightRef = useRef(null);

  const sorted = useMemo(() => {
    const items = hauls.map((h, i) => ({ ...h, _origIdx: i }));
    items.sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (typeof va === 'string') {
        va = va.toLowerCase();
        vb = vb.toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return items;
  }, [hauls, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  useEffect(() => {
    if (highlightedHaul != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [highlightedHaul]);

  const thClass = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? 'sorted-asc' : 'sorted-desc';
  };

  const fmtVol = (ft) => {
    if (bblPerIn > 0) return `${(ft * bblPerIn).toFixed(1)} bbl`;
    return `${ft.toFixed(1)} in`;
  };

  // Detected haul volume in barrels, using the current tank config (bblPerIn
  // already includes numTanks). Compared against the ground-truth ticket bbls.
  const detectedBbls = (h) => (bblPerIn > 0 ? h.haul_volume_ft * bblPerIn : null);

  const errorInfo = (h) => {
    if (h.ticket == null || h.ticket_bbls == null || bblPerIn <= 0) return null;
    const det = detectedBbls(h);
    const errBbl = det - h.ticket_bbls;
    const errPct = h.ticket_bbls ? (100 * errBbl) / h.ticket_bbls : null;
    const absPct = Math.abs(errPct);
    // green < 5%, amber < 15%, red otherwise
    const level = absPct < 5 ? 'ok' : absPct < 15 ? 'warn' : 'bad';
    return { errBbl, errPct, level };
  };

  const fmtRate = (ftPerHr) => {
    if (bblPerIn > 0) return `${(ftPerHr * bblPerIn).toFixed(2)} bbl/hr`;
    return `${ftPerHr.toFixed(3)} in/hr`;
  };

  const deactivatedCount = sorted.filter(h => h._deactivated).length;

  return (
    <div className="haul-table-card">
      <div className="haul-table-header">
        <h2>Haul Events ({sorted.length - deactivatedCount})</h2>
        {onToggleShowDeactivated && (
          <label className="show-deact-toggle">
            <input
              type="checkbox"
              checked={!!showDeactivated}
              onChange={onToggleShowDeactivated}
            />
            Show deactivated{deactivatedCount > 0 ? ` (${deactivatedCount})` : ''}
          </label>
        )}
      </div>
      <div className="haul-table-wrap">
        <table>
          <thead>
            <tr>
              <th className={thClass('drop_start')} onClick={() => handleSort('drop_start')}>
                Date/Time
              </th>
              <th className={thClass('haul_volume_ft')} onClick={() => handleSort('haul_volume_ft')}>
                Volume (detected)
              </th>
              <th className={thClass('ticket_bbls')} onClick={() => handleSort('ticket_bbls')}>
                Ticket
              </th>
              <th className={thClass('trucking')} onClick={() => handleSort('trucking')}>
                Company
              </th>
              <th>Error vs Ticket</th>
              <th className={thClass('level_before_ft')} onClick={() => handleSort('level_before_ft')}>
                Level Before
              </th>
              <th className={thClass('level_after_ft')} onClick={() => handleSort('level_after_ft')}>
                Level After
              </th>
              <th className={thClass('pre_slope_ft_per_hr')} onClick={() => handleSort('pre_slope_ft_per_hr')}>
                Fill Rate Before
              </th>
              <th className={thClass('post_slope_ft_per_hr')} onClick={() => handleSort('post_slope_ft_per_hr')}>
                Fill Rate After
              </th>
              <th className={thClass('pre_r_squared')} onClick={() => handleSort('pre_r_squared')}>
                Fit R²
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((h) => {
              const isHighlighted = highlightedHaul === h._origIdx;
              return (
                <tr
                  key={h._origIdx}
                  ref={isHighlighted ? highlightRef : null}
                  className={`${isHighlighted ? 'highlighted' : ''} ${h._deactivated ? 'row-deactivated' : ''}`}
                  onClick={() => onHaulClick(isHighlighted ? null : h._origIdx)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{formatDateTime(h.drop_start)}</td>
                  <td style={{ fontWeight: 600 }}>{fmtVol(h.haul_volume_ft)}</td>
                  {(() => {
                    const err = errorInfo(h);
                    if (h.ticket == null || h.ticket_bbls == null) {
                      return (
                        <>
                          <td className="ticket-cell ticket-none">—</td>
                          <td className="company-cell company-none">—</td>
                          <td className="error-cell error-none">no ticket</td>
                        </>
                      );
                    }
                    return (
                      <>
                        <td className="ticket-cell">
                          {h.ticket_bbls.toFixed(1)} bbl
                          <span className="ticket-id">#{h.ticket}</span>
                        </td>
                        <td className="company-cell">{h.trucking || '—'}</td>
                        <td className={`error-cell error-${err.level}`}>
                          {err.errBbl >= 0 ? '+' : ''}{err.errBbl.toFixed(1)} bbl
                          <span className="error-pct">
                            ({err.errPct >= 0 ? '+' : ''}{err.errPct.toFixed(0)}%)
                          </span>
                        </td>
                      </>
                    );
                  })()}
                  <td>{fmtVol(h.level_before_ft)}</td>
                  <td>{fmtVol(h.level_after_ft)}</td>
                  <td>{fmtRate(h.pre_slope_ft_per_hr)}</td>
                  <td>{fmtRate(h.post_slope_ft_per_hr)}</td>
                  <td>
                    {isFinite(h.pre_r_squared) ? h.pre_r_squared.toFixed(2) : '—'}
                    {' / '}
                    {isFinite(h.post_r_squared) ? h.post_r_squared.toFixed(2) : '—'}
                  </td>
                  <td className="deact-cell">
                    {onToggleHaul && (
                      <button
                        className={`deact-btn ${h._deactivated ? 'on' : ''}`}
                        title={h._deactivated ? 'Reactivate this event' : 'Deactivate this event (bad reading)'}
                        onClick={(e) => { e.stopPropagation(); onToggleHaul(h); }}
                      >
                        {h._deactivated ? 'Off' : '✕'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default HaulTable;
